/**
 * Auth store using Zustand + first-party Signa API auth.
 *
 * The backend issues Signa-owned JWTs from `/auth/login` and `/auth/register`.
 * The token is stored locally and sent to API calls as `Authorization: Bearer`.
 */

import { create } from 'zustand';
import { API_BASE } from '../lib/api-config';

const AUTH_API_BASE = `${API_BASE}/auth`;
const TOKEN_STORAGE_KEY = 'signa.auth.token';
const USER_STORAGE_KEY = 'signa.auth.user';
const ENCRYPTED_DEK_STORAGE_KEY = 'signa.auth.encryptedDEK';
const DEMO_TOKEN = 'demo-token';

const DEMO_USER: AuthUser = { id: 'demo-user', email: 'demo@signa.app', name: 'Demo User' };

const DEMO_CONNECTIONS: ProviderConnection[] = [
  {
    id: 'demo-1',
    provider: 'gmail',
    email: 'demo@gmail.com',
    scopes: ['mail'],
    isActive: true,
    connectedAt: new Date().toISOString(),
    lastSyncAt: new Date().toISOString(),
  },
  {
    id: 'demo-2',
    provider: 'microsoft',
    email: 'demo@outlook.com',
    scopes: ['mail'],
    isActive: true,
    connectedAt: new Date().toISOString(),
    lastSyncAt: new Date().toISOString(),
  },
];

export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

export interface ProviderConnection {
  id: string;
  provider: 'gmail' | 'microsoft';
  email: string;
  scopes: string[];
  isActive: boolean;
  connectedAt: string;
  lastSyncAt: string | null;
}

interface AuthResponse {
  success?: boolean;
  token?: string;
  user?: AuthUser;
  encryptedDEK?: string;
  error?: string;
  message?: string;
}

interface AuthState {
  user: AuthUser | null;
  /** Current Signa JWT - used as Bearer token for API calls */
  token: string | null;
  encryptedDEK: string | null;
  isLoading: boolean;
  error: string | null;
  connections: ProviderConnection[];

  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => Promise<void>;
  demoLogin: () => void;
  /** Initialise auth state from localStorage and validate it with /auth/me */
  loadFromStorage: () => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;
  fetchConnections: () => Promise<void>;
  setConnections: (connections: ProviderConnection[]) => void;
}

function readStoredAuth(): Pick<AuthState, 'token' | 'user' | 'encryptedDEK'> {
  if (typeof window === 'undefined') {
    return { token: null, user: null, encryptedDEK: null };
  }

  const token = window.localStorage.getItem(TOKEN_STORAGE_KEY);
  const encryptedDEK = window.localStorage.getItem(ENCRYPTED_DEK_STORAGE_KEY);
  const userJson = window.localStorage.getItem(USER_STORAGE_KEY);

  let user: AuthUser | null = null;
  if (userJson) {
    try {
      user = JSON.parse(userJson) as AuthUser;
    } catch {
      user = null;
    }
  }

  return { token, user, encryptedDEK };
}

function persistAuth(token: string, user: AuthUser, encryptedDEK: string | null) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  if (encryptedDEK) {
    window.localStorage.setItem(ENCRYPTED_DEK_STORAGE_KEY, encryptedDEK);
  } else {
    window.localStorage.removeItem(ENCRYPTED_DEK_STORAGE_KEY);
  }
}

function clearStoredAuth() {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(USER_STORAGE_KEY);
  window.localStorage.removeItem(ENCRYPTED_DEK_STORAGE_KEY);
}

function isDemoToken(token: string | null): boolean {
  return token === DEMO_TOKEN;
}

async function parseAuthResponse(res: Response): Promise<AuthResponse> {
  const data = (await res.json().catch(() => ({}))) as AuthResponse;
  if (!res.ok) {
    throw new Error(data.message ?? data.error ?? 'Authentication failed');
  }
  if (!data.token || !data.user) {
    throw new Error('Authentication response was missing token or user');
  }
  return data;
}

async function postAuth(path: '/login' | '/register', body: Record<string, string>): Promise<AuthResponse> {
  const res = await fetch(`${AUTH_API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return parseAuthResponse(res);
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  token: null,
  encryptedDEK: null,
  isLoading: false,
  error: null,
  connections: [],

  login: async (email: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const data = await postAuth('/login', { email, password });
      persistAuth(data.token!, data.user!, data.encryptedDEK ?? null);
      set({
        token: data.token!,
        user: data.user!,
        encryptedDEK: data.encryptedDEK ?? null,
        isLoading: false,
      });
      void get().fetchConnections();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Login failed';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  register: async (email: string, password: string, name: string) => {
    set({ isLoading: true, error: null });

    try {
      const data = await postAuth('/register', { email, password, name });
      persistAuth(data.token!, data.user!, data.encryptedDEK ?? null);
      set({
        token: data.token!,
        user: data.user!,
        encryptedDEK: data.encryptedDEK ?? null,
        isLoading: false,
      });
      void get().fetchConnections();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registration failed';
      set({ isLoading: false, error: message });
      throw err;
    }
  },

  logout: async () => {
    const { token } = get();
    try {
      if (token) {
        await fetch(`${AUTH_API_BASE}/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } finally {
      clearStoredAuth();
      set({ token: null, user: null, encryptedDEK: null, error: null, connections: [] });
    }
  },

  demoLogin: () => {
    persistAuth(DEMO_TOKEN, DEMO_USER, null);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('signa:demo-mode'));
    }
    set({
      user: DEMO_USER,
      token: DEMO_TOKEN,
      encryptedDEK: null,
      isLoading: false,
      error: null,
      connections: DEMO_CONNECTIONS,
    });
  },

  loadFromStorage: async () => {
    const stored = readStoredAuth();
    if (!stored.token || !stored.user) {
      set({ token: null, user: null, encryptedDEK: null, connections: [] });
      return;
    }

    if (isDemoToken(stored.token)) {
      set({ token: DEMO_TOKEN, user: DEMO_USER, encryptedDEK: null, connections: DEMO_CONNECTIONS });
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('signa:demo-mode'));
      }
      return;
    }

    set(stored);
    const ok = await get().checkAuth();
    if (!ok) {
      clearStoredAuth();
      set({ token: null, user: null, encryptedDEK: null, connections: [] });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  checkAuth: async () => {
    const { token } = get();
    if (!token) return false;
    if (isDemoToken(token)) {
      set({ token: DEMO_TOKEN, user: DEMO_USER, encryptedDEK: null, connections: DEMO_CONNECTIONS });
      return true;
    }

    try {
      const res = await fetch(`${AUTH_API_BASE}/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) return false;

      const data = (await res.json()) as { user: AuthUser };
      set({ user: data.user });
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(data.user));
      }
      return true;
    } catch {
      return false;
    }
  },

  fetchConnections: async () => {
    const { token } = get();
    if (!token) {
      set({ connections: [] });
      return;
    }

    if (isDemoToken(token)) {
      set({ connections: DEMO_CONNECTIONS });
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/provider-connections`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        set({ connections: data.connections ?? [] });
      } else {
        set({ connections: [] });
      }
    } catch {
      set({ connections: [] });
    }
  },

  setConnections: (connections: ProviderConnection[]) => {
    set({ connections });
  },
}));

/**
 * Hook to get the current Signa JWT for API calls.
 * Returns null if not authenticated.
 */
export function useAuthToken(): string | null {
  return useAuthStore((s) => s.token);
}

/**
 * Hook to check if the user is authenticated.
 */
export function useIsAuthenticated(): boolean {
  return useAuthStore((s) => s.user !== null && s.token !== null);
}
